import React, { useContext, useMemo, useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Outlet, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Sun, Moon, Monitor, Gauge, Search, Clock } from "lucide-react";
import { AuthContext } from "@/App";
import { EcotileCommandPalette, EcotileMentionInbox, ecoBus } from "@/components/EcotileHeaderTools";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Ecotile shell — the flooring-division portal. A sibling app to the ERP
// <Layout> and a peer of StockLayout: it does NOT load the ERP data contexts,
// but wears a faithful RockLink header (RockLink wordmark + frosted user pill)
// so it reads as ONE product. The Ecotile identity is the brand YELLOW #FFCC00
// on a dark-grey #3F4444 base (the FY tracking sheet's own idiom) — carried by
// the badge + keyline here and heavily inside EcotilePage.

// Self-contained theme controller — mirrors the ERP / Stock Center machinery
// (light/auto/dark + flat) so Ecotile-only users, who never mount MainApp, get
// the SAME appearance controls. Persists to the shared rl_theme / rl_flat keys.
function applyThemePref(pref) {
  let dark = false;
  if (pref === "dark") dark = true;
  else if (pref === "system") { try { dark = window.matchMedia("(prefers-color-scheme: dark)").matches; } catch {} }
  const el = document.documentElement;
  el.classList.remove("light", "dark");
  el.classList.add(dark ? "dark" : "light");
}
function useEcotileTheme() {
  const [themePref, setPref] = useState(() => {
    try { const s = localStorage.getItem("rl_theme"); return ["light", "dark", "system"].includes(s) ? s : "system"; }
    catch { return "system"; }
  });
  const [flatMode, setFlat] = useState(() => {
    try { return localStorage.getItem("rl_flat") === "1"; } catch { return false; }
  });
  const prefRef = useRef(themePref);
  useEffect(() => { prefRef.current = themePref; }, [themePref]);
  useEffect(() => { applyThemePref(themePref); }, [themePref]);
  useEffect(() => {
    if (flatMode) document.documentElement.setAttribute("data-flat", "1");
    else document.documentElement.removeAttribute("data-flat");
  }, [flatMode]);
  useEffect(() => {
    if (themePref !== "system") return;
    let mq; const apply = () => applyThemePref("system");
    try { mq = window.matchMedia("(prefers-color-scheme: dark)"); mq.addEventListener?.("change", apply); } catch {}
    return () => { try { mq?.removeEventListener?.("change", apply); } catch {} };
  }, [themePref]);
  const setThemePref = useCallback((p) => { setPref(p); try { localStorage.setItem("rl_theme", p); } catch {} }, []);
  const cycleTheme = useCallback(() => {
    const order = ["light", "system", "dark"];
    const next = order[(order.indexOf(prefRef.current) + 1) % order.length];
    setThemePref(next);
  }, [setThemePref]);
  const toggleFlat = useCallback(() => {
    setFlat((v) => { const nv = !v; try { localStorage.setItem("rl_flat", nv ? "1" : "0"); } catch {} return nv; });
  }, []);
  return { themePref, setThemePref, cycleTheme, flatMode, toggleFlat };
}

function ThemeSlider({ themePref, setThemePref }) {
  const opts = [
    { key: "light", label: "Light", Icon: Sun },
    { key: "system", label: "Auto", Icon: Monitor },
    { key: "dark", label: "Dark", Icon: Moon },
  ];
  return (
    <div className="px-3 py-2" data-testid="eco-theme-slider">
      <div className="flex items-center gap-1 rounded-full bg-[hsl(var(--muted))]/60 p-1">
        {opts.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setThemePref(key)}
            className={`flex-1 flex items-center justify-center gap-1.5 h-7 rounded-full text-xs font-medium transition-colors ${
              themePref === key
                ? "bg-[hsl(var(--card))] text-[hsl(var(--foreground))] shadow-sm"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            }`}
            data-testid={`eco-theme-${key}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>
      {themePref === "system" && (
        <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1.5 text-center whitespace-nowrap">Follows your device setting</p>
      )}
    </div>
  );
}

function FlatModeRow({ flatMode, toggleFlat }) {
  return (
    <div className="px-3 pt-1.5 pb-2" data-testid="eco-flat-mode-row">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm whitespace-nowrap text-[hsl(var(--foreground))]">
          <Gauge className="w-4 h-4 shrink-0 text-[hsl(var(--muted-foreground))]" /> Reduce visual effects
        </span>
        <Switch checked={flatMode} onCheckedChange={toggleFlat} aria-label="Reduce visual effects" data-testid="eco-flat-mode-switch" />
      </div>
      <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1 pl-6">Snappier on older devices</p>
    </div>
  );
}

const ROLE_BADGE = {
  Admin: "bg-orange-500/15 text-orange-500 border-orange-500/30",
  Ecotile: "bg-yellow-400/20 text-yellow-700 dark:text-yellow-300 border-yellow-500/40",
};

export default function EcotileLayout() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [hint, setHint] = useState(null); // { key, label, color, leaving }
  const hintT = useRef({});
  const hintSeq = useRef(0); // increments on every press so the keycap animation replays even for repeated keys
  const { themePref, setThemePref, cycleTheme, flatMode, toggleFlat } = useEcotileTheme();

  // Flash a keycap hint at top-header-center for ~1.6s so a pressed shortcut is
  // learnable. Re-firing clears the prior one and re-drops instantly.
  const flashHint = useCallback((key, label, color, theme) => {
    clearTimeout(hintT.current.life); clearTimeout(hintT.current.gone);
    hintSeq.current += 1;
    setHint({ key, label, color: color || null, theme: theme || null, leaving: false, seq: hintSeq.current });
    hintT.current.life = setTimeout(() => {
      setHint((h) => (h ? { ...h, leaving: true } : h));
      hintT.current.gone = setTimeout(() => setHint(null), 260);
    }, 1600);
  }, []);
  useEffect(() => () => { clearTimeout(hintT.current.life); clearTimeout(hintT.current.gone); }, []);

  const initials = useMemo(() => {
    const n = (user?.name || user?.email || "?").trim();
    const parts = n.split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || n[0]?.toUpperCase() || "?";
  }, [user]);
  const firstName = (user?.name || user?.email || "").split(/\s+/)[0];

  // Dual-access users have an ERP footing; Ecotile-only (role "Ecotile") must
  // NOT see a way into the ERP.
  const isDualAccess = user?.role && user.role !== "Ecotile";

  useEffect(() => {
    const prev = document.title;
    document.title = "Ecotile — Rockpecker Flooring";
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      // ⌘K / Ctrl-K opens magic search from anywhere (works even inside fields)
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) { e.preventDefault(); setCmdOpen(true); return; }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Single-key shortcuts must NEVER fire while the user is typing — in the
      // search box, a note/mention input, or any editable field — otherwise they
      // can't type their query. Mirrors the metallic Layout guard exactly.
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      // …and not while the palette is open (it owns its own keys).
      if (cmdOpen) return;
      if (e.key === "/") { e.preventDefault(); setCmdOpen(true); return; }   // "/" opens magic search
      const k = e.key.toLowerCase();
      // Stage / view jumps — [view, label, stage colour] — same letters as the palette hint badges.
      const VIEW = {
        h: ["dashboard", "Dashboard", "#B78E10"], r: ["PO Received", "PO Received", "#3b82f6"],
        p: ["Procurement", "Procurement", "#f97316"], m: ["Manufacturing", "Manufacturing", "#a855f7"],
        q: ["Quality Control", "Quality Control", "#14b8a6"], d: ["Dispatch", "Dispatch", "#f59e0b"],
        c: ["Completed", "Completed", "#22c55e"], g: ["register", "Register", "#6e5600"], l: ["history", "History log", "#6e5600"],
      };
      if (VIEW[k]) { const [view, label, color] = VIEW[k]; e.preventDefault(); ecoBus.emit({ type: "view", view }); flashHint(k.toUpperCase(), label, color); return; }
      // Attention filters (jump into the register with the filter applied).
      const FILTER = { o: ["overdue", "Overdue payments"], k: ["nodl", "No deadline"], f: ["fu", "Follow-up"], y: ["dispp", "Dispatch pending"], x: ["all", "Filters cleared"] };
      if (FILTER[k]) { const [filter, label] = FILTER[k]; e.preventDefault(); ecoBus.emit({ type: "registerFilter", filter }); flashHint(k.toUpperCase(), label, null); return; }
      if (k === "n") {
        e.preventDefault(); cycleTheme();
        const order = ["light", "system", "dark"]; const next = order[(order.indexOf(themePref) + 1) % 3];
        flashHint("N", "Theme", null, next); return;   // rail hint glides to `next`
      }
      if (k === "z") { e.preventDefault(); toggleFlat(); flashHint("Z", flatMode ? "Effects restored" : "Reduced effects", null); return; }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [cycleTheme, toggleFlat, cmdOpen, themePref, flatMode, flashHint]);

  return (
    <div className="min-h-screen rl-canvas" data-testid="ecotile-layout">
      {/* Neutral RockLink header (reads as one product) + Ecotile yellow keyline
          and badge for identity. */}
      <header className="sticky top-0 z-40 border-b-2 border-[#FFCC00] bg-[hsl(var(--card))]/95 backdrop-blur">
        <div className="relative max-w-[1440px] mx-auto px-3 md:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0" data-testid="eco-brand">
            {/* ecotile logo (replaces the rockpecker mark) — no rounded corner */}
            <img src="/ecotile-weblogo-xsmall.png" alt="ecotile" className="h-9 w-auto shrink-0 select-none" draggable={false} />
            {/* RockLink wordmark + Ecotile subtitle (same two-line block as the metallic header) */}
            <div className="hidden sm:flex flex-col pl-3 border-l border-[hsl(var(--border))]">
              <div className="flex items-center gap-2">
                <span className="text-lg font-extrabold tracking-tight leading-none" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.01em" }}>
                  <span style={{ color: "#ef4444" }}>Rock</span><span style={{ color: "#f97316" }}>Link</span>
                </span>
                <span className="text-[10px] font-mono-data font-semibold tracking-wide text-[hsl(var(--muted-foreground))]/60">v1</span>
              </div>
              <p className="text-[9px] font-semibold tracking-widest uppercase leading-none mt-0.5 text-[hsl(var(--muted-foreground))]">
                Ecotile Flooring
              </p>
            </div>
            {/* Switch to Metallic — sits right of the logo */}
            {isDualAccess && (
              <button
                onClick={() => navigate("/")}
                className="rl-frost-btn hidden lg:flex items-center gap-1.5 h-8 px-3 ml-2 rounded-full text-[12px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                data-testid="eco-switch-to-erp"
                title="Switch to the metallic RockLink ERP"
              >
                Switch to Metallic <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Magic search — collapsed round button; expands from the top on
                click (no screen dim), exactly like the metallic launcher. */}
            <button
              onClick={() => setCmdOpen(true)}
              data-testid="eco-search"
              className={`relative flex items-center justify-center w-8 h-8 rounded-full transition-[colors,box-shadow] duration-150 ${cmdOpen ? "bg-zinc-200 dark:bg-zinc-800 text-[hsl(var(--muted-foreground))] [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]" : "text-white rl-search-glass"}`}
              aria-label="Search" title="Search  (⌘K or /)"
            >
              <Search className="w-4 h-4" />
            </button>
            {/* @mentions inbox */}
            <EcotileMentionInbox />
            {/* History log */}
            <button
              onClick={() => ecoBus.emit({ type: "view", view: "history" })}
              className="rl-frost-btn flex items-center justify-center w-8 h-8 rounded-full text-[hsl(var(--muted-foreground))]"
              data-testid="eco-history" aria-label="History log" title="History log"
            >
              <Clock className="w-4 h-4" />
            </button>
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-1.5 h-8 pl-1 pr-2.5 rounded-full text-[hsl(var(--muted-foreground))] transition-[colors,box-shadow] duration-150 ${
                    menuOpen ? "bg-zinc-200 dark:bg-zinc-800 [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]" : "rl-frost-btn"
                  }`}
                  data-testid="eco-user-menu" aria-label="User menu"
                >
                  <span className="flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold shrink-0"
                        style={{ background: "linear-gradient(135deg,#FFCC00,#E6B800)", color: "#2b2500" }}>{initials}</span>
                  <span className="hidden sm:inline text-sm font-medium text-[hsl(var(--foreground))]">{firstName}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${menuOpen ? "rotate-180" : ""}`} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 rl-frost rl-hero-glass">
                <div className="px-3 py-2.5">
                  <p className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">{user?.name || user?.email}</p>
                  <span className={`mt-1 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${ROLE_BADGE[user?.role] || "bg-slate-500/15 text-slate-500 border-slate-500/30"}`}>
                    {user?.role}
                  </span>
                </div>
                <DropdownMenuSeparator />
                <ThemeSlider themePref={themePref} setThemePref={setThemePref} />
                <FlatModeRow flatMode={flatMode} toggleFlat={toggleFlat} />
                <p className="px-3 pb-1 text-[10px] text-[hsl(var(--muted-foreground))]/70">Press <kbd className="font-mono-data">N</kbd> to cycle theme</p>
                {isDualAccess && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/")}><ArrowRight className="w-4 h-4 mr-2" /> Switch to Metallic ERP</DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-[hsl(var(--destructive))]">Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <Outlet />
      </main>

      {cmdOpen && <EcotileCommandPalette onClose={() => setCmdOpen(false)} viewControls={{ themePref, setThemePref, flatMode, toggleFlat }} />}

      {/* Shortcut keycap hint — drops from under the header at dead centre, the
          same spot magic-search expands from. Light silver cap / dark backlit
          cap follow the theme (see .eco-key-* in App.css). */}
      {hint && createPortal(
        <div className={`rl-key-hint${hint.leaving ? " rl-key-hint--leaving" : ""}`} data-testid="eco-shortcut-hint" role="status" aria-live="polite">
          <kbd key={hint.seq} className="rl-keycap">{hint.key}</kbd>
          {hint.theme ? (
            /* N → segmented Light/Auto/Dark rail; the thumb glides to the new mode */
            <div className="rl-theme-rail" data-testid="eco-hint-theme-rail">
              <span className="rl-theme-thumb" style={{ "--i": `${["light", "system", "dark"].indexOf(hint.theme) * 66}px` }} />
              {[["light", "Light", Sun], ["system", "Auto", Monitor], ["dark", "Dark", Moon]].map(([k, lbl, Ic]) => (
                <span key={k} className={`rl-theme-seg${k === hint.theme ? " on" : ""}`}><Ic className="w-[13px] h-[13px]" />{lbl}</span>
              ))}
            </div>
          ) : (
            <>
              {hint.color && <span className="rl-key-dot" style={{ background: hint.color }} />}
              <span className="rl-key-lbl">{hint.label}</span>
            </>
          )}
          <span key={`bar-${hint.seq}`} className="rl-key-bar" />
        </div>,
        document.body
      )}
    </div>
  );
}
